/**
 * Client-side mirror of ``app/services/company_ids.py`` (KAN-924).
 *
 * The server is the authority — this only tells the customer *before*
 * the 422 why the order will not go through. Both sides are registries
 * keyed by country, not ``if (country === "CZ")`` chains
 * (``docs/GENERIC_PLATFORM.md``); a country the table does not know
 * has no rule and passes.
 */

/** 8 digits, mod-11 check digit weighted 8…2 — the CZ/SK ``IČO``. */
function isMod11CompanyId(cleaned: string): boolean {
  if (!/^\d{1,8}$/.test(cleaned)) return false;
  const digits = cleaned.padStart(8, "0").split("").map(Number);
  const weights = [8, 7, 6, 5, 4, 3, 2];
  const weighted = digits
    .slice(0, 7)
    .reduce((sum, d, i) => sum + d * weights[i], 0);
  const remainder = weighted % 11;
  const expected = remainder === 0 ? 1 : remainder === 1 ? 0 : 11 - remainder;
  return digits[7] === expected;
}

/** Country → company-id rule. Extend by adding a row. */
export const COMPANY_ID_RULES: Record<string, (cleaned: string) => boolean> =
  {
    CZ: isMod11CompanyId,
    SK: isMod11CompanyId,
  };

/**
 * Country → the company register the platform can actually look a
 * company up in (KAN-1522).
 *
 * Same shape and same reason as ``COMPANY_ID_RULES``: a table, not an
 * ``if (country === "CZ")`` chain. ARES is the CZECH register, so
 * offering „Vyhledat v ARES" to a Slovak company promises a lookup that
 * cannot succeed — the row simply is not there. Změřeno při průchodu
 * test5: pokladna se zemí SK to tlačítko nabízela.
 *
 * A country without a row gets no lookup button and fills the fields in
 * by hand, which is what happens today for every country but CZ anyway.
 * Až přibude slovenský registr, přibude sem řádek — ne podmínka v UI.
 */
export const COMPANY_REGISTRY_LOOKUPS: Record<string, string> = {
  CZ: "ARES",
};

/** Name of the register for this country, or ``null`` when we have none. */
export function companyRegistryFor(
  countryCode: string | null | undefined,
): string | null {
  return COMPANY_REGISTRY_LOOKUPS[(countryCode ?? "").toUpperCase()] ?? null;
}

/**
 * Per-country VAT number formats (number part after the 2-letter
 * prefix). Same table as the VIES service on the backend.
 */
export const VAT_ID_FORMATS: Record<string, RegExp> = {
  AT: /^U\d{8}$/,
  BE: /^0?\d{9,10}$/,
  BG: /^\d{9,10}$/,
  CY: /^\d{8}[A-Z]$/,
  CZ: /^\d{8,10}$/,
  DE: /^\d{9}$/,
  DK: /^\d{8}$/,
  EE: /^\d{9}$/,
  EL: /^\d{9}$/,
  ES: /^[A-Z0-9]\d{7}[A-Z0-9]$/,
  FI: /^\d{8}$/,
  FR: /^[A-HJ-NP-Z0-9]{2}\d{9}$/,
  HR: /^\d{11}$/,
  HU: /^\d{8}$/,
  IE: /^\d{7}[A-Z]{1,2}$|^\d[A-Z+*]\d{5}[A-Z]$/,
  IT: /^\d{11}$/,
  LT: /^\d{9}$|^\d{12}$/,
  LU: /^\d{8}$/,
  LV: /^\d{11}$/,
  MT: /^\d{8}$/,
  NL: /^\d{9}B\d{2}$/,
  PL: /^\d{10}$/,
  PT: /^\d{9}$/,
  RO: /^\d{2,10}$/,
  SE: /^\d{12}$/,
  SI: /^\d{8}$/,
  SK: /^\d{10}$/,
  XI: /^\d{9}$|^\d{12}$/,
};

function stripSpaces(raw: string): string {
  return raw.replace(/\s+/g, "");
}

/** Blank passes (the field is optional); unknown country has no rule. */
export function isValidCompanyId(
  raw: string,
  countryCode: string | null | undefined,
): boolean {
  const cleaned = stripSpaces(raw);
  if (!cleaned) return true;
  const rule = COMPANY_ID_RULES[(countryCode ?? "").toUpperCase()];
  return rule ? rule(cleaned) : true;
}

/**
 * Blank passes; the prefix picks the rule; an unknown prefix only has
 * to match the generic shape (2 letters + 2–13 alphanumerics).
 */
export function isValidVatId(raw: string): boolean {
  const cleaned = stripSpaces(raw).toUpperCase();
  if (!cleaned) return true;
  if (!/^[A-Z]{2}[A-Z0-9]{2,13}$/.test(cleaned)) return false;
  const format = VAT_ID_FORMATS[cleaned.slice(0, 2)];
  return format ? format.test(cleaned.slice(2)) : true;
}
